/**
 * UpSurv 2.0 — the question catalog (specification §3).
 *
 * Every question the surveyor can be asked lives here exactly once, keyed by its
 * specification ID. Those IDs are STABLE: the pricing catalog and the future
 * estimate engine key line items off them, so a question is never renamed and
 * never renumbered. Adding is fine; changing an existing ID is not.
 *
 * ── Structure vs. answers ────────────────────────────────────────────────────
 * M1/M2/M3/M6/M7 appear here so the room-setup screen can render from one list,
 * but they are STRUCTURAL: their values live in the entity tree (MachineRoom,
 * Group, Hoistway, CarRef), never in `SurveyDocument.answers`. The rest of the
 * form is generated from them, so they have to be canonical structure rather
 * than answers. See STRUCTURAL_QUESTION_IDS below — the form must not write
 * those IDs through answerKey().
 *
 * ── Two specification rows that are headers, not questions ───────────────────
 * §3.3 rows "E4 — Shunt logic" and "E9 — LED strip lighting" introduce their
 * lettered children (E4a–E4c, E9a–E9d); neither has a type, a pricing mode, or
 * anything for the surveyor to answer. They are not emitted as QuestionDefs —
 * the gate they describe is carried on the children instead (E4* all gate on
 * M4). Reported to the design authority as a resolved ambiguity.
 *
 * ── The question posed is the question answered (D-015) ──────────────────────
 * Every `flagCount` question renders as YES/NO, and only after Yes does a
 * "How many?" follow-up appear. So the LABEL of a `flagCount` question must be a
 * yes/no question. "Mainline disconnects — how many get replaced with new?" over
 * a pair of Yes/No buttons is the defect the design authority reported on
 * 2026-08-06: the surveyor is asked for a number and offered a choice.
 *
 * Labels and hints are free to change and are written to be READ ALOUD; ids are
 * not. If you are tempted to rename an id to match a reworded label, don't — the
 * pricing catalog and the estimate engine key line items off it.
 *
 * ── Chip's wording round (D-045, 2026-09-11) ─────────────────────────────────
 * The first content change since rule zero pinned this file (D-028): seven
 * edits read off Chip's handwritten notes after he field-tested production.
 * F21, E9a and B6 reworded; L3 from a Yes/No + count to a plain count; L3a
 * added after it; F19 deleted; and the lobby shot `lobby.detector_egress`
 * relabelled in sections.ts. Ids on the rewords are kept (above); F19's stored
 * answers are kept too — the cut sheet still prints them, nothing asks it.
 * Rule zero holds for everything not in that list. D-046, the same day, is
 * Chip's answers to the two questions that round raised: E9b, E9c and L3 are
 * deleted as well (redundant beside E9a; "fixtures" read as if OSB supplied
 * them), and the "Main Egress Lobby" card's caption is gone (flsShots.ts).
 *
 * ── Chip's answers, 2026-10-27 (D-053) ──────────────────────────────────────
 * P12 (paint the pit) and P14 (fire caulking, pit) are deleted — the pit's
 * paint is the elevator company's, and O8 already caulks the hoistway. A1
 * reads "crane assist", A2 (picks) is required under it, H3 says
 * "Refrigerant line", and E5b's hint says one cab light disconnect per car.
 * Ids kept on every reword; the two deleted ids are never reused.
 *
 * ── Chip's follow-ups, 2026-10-27 (D-054) ───────────────────────────────────
 * Six children, each right after its parent and gated on its Yes: G8a / O6a
 * (is the equipment still in use and has to stay?), G9a (feet of fence), G9b
 * (gate?), G14a (feet of railing, Traction only like G14), A3a (4x8 plywood
 * sheets). All INFO and required when shown, the A2 precedent: the pricing
 * catalog holds the price. B12 gains `max: 10` — a typed count cap.
 *
 * ── ICRA barricades that move, 2026-10-30 (D-058) ────────────────────────────
 * OSB owns its ICRA barricades and rents them to the job. When the same set
 * comes down and goes back up on another elevator, that is 4 labor hours per
 * opening on each later elevator (Chip, Thomas approved). Three children
 * after L9c: L9d (will they move to another elevator? — asked on L9a = ICRA),
 * then on its Yes L9f (how many cars are worked on at the same time, 1 to 50
 * — the barricades rented are landings x this, Stephanie 2026-10-30) and L9e
 * (after those first cars, how many more elevators, 1 to 50). All INFO and required when shown, the A2 /
 * D-054 precedent: the pricing catalog holds the price. L9d opens blank.
 * L9a's ICRA option hint no longer claims a move cost nothing priced.
 *
 * ── Form sets ────────────────────────────────────────────────────────────────
 * `formSets` absent means the question is asked in both sets. G11–G17 are
 * Traction-only; E14 and P6 are Hydro-only. MRL rooms run the Hydro set with an
 * MRL tag — formSetFor() in types.ts resolves that, so nothing here mentions MRL.
 *
 * ── One block spans five sections: FLS (§3.11, Amendments 5 and 6) ──────────
 * Every other block below is one section at one entity level. Fire Life Safety
 * spans five: the panel and its paperwork (F1–F5) are asked ONCE, on the
 * survey-level `fls` section at the end of the walk (Amendment 6, 2026-08-18),
 * and everything that lives in a machine room, a shaft, a pit or a lobby is
 * filed in the section that repeats at its own level — electrical, overhead,
 * pit, lobby. The `FLS` array near the bottom is where they all live; its
 * header explains the id scheme and the move.
 */

import type {
  AnswerValue,
  FormSet,
  QuestionCondition,
  QuestionDef,
  QuestionOption,
  Scope,
  SectionKind,
} from './types';

/**
 * IDs whose value lives in the entity tree, not in `answers`. The form renders
 * them from this catalog but persists them structurally (spec §1, brief §3).
 */
export const STRUCTURAL_QUESTION_IDS: readonly string[] = ['M1', 'M2', 'M3', 'M6', 'M7'];

/**
 * `auto: true` covers two different things, and the difference matters:
 *
 *   a) FULLY DERIVED — the surveyor supplies nothing at all. B17 counts ATS
 *      rooms, E6a is one CAT6 per car, L1 is the group's landings. These render
 *      as a "⚙ calculated for you" chip with no control, must NEVER appear in
 *      `SurveyDocument.answers`, and can never be a gap: there is nothing for a
 *      surveyor to have failed to answer.
 *   b) AUTO QUANTITY off a surveyor input — E6 and E8 and L8 are a Y/N the
 *      surveyor answers whose QUANTITY is computed; E7c and H3 take a measured
 *      distance and compute 25-ft units. types.ts spells this out on
 *      QuestionDef.auto. These DO carry an answer.
 *
 * QuestionDef has one flag for both, so the (a) set is enumerated here. Gap
 * detection and the form renderer must consult it — treating a fully-derived
 * question as unanswered would flag a gap on every clean survey.
 */
export const DERIVED_ONLY_QUESTION_IDS: readonly string[] = [
  'B14',
  'B17',
  'E4b',
  'E4c',
  'E6a',
  'E9d',
  // F1 — FLS back boxes, one per landing across every group in the building.
  // Nothing is asked and nothing is gated: it is counted off the group
  // definitions (M6) the same way L1 is. Leaving it out of this list would flag
  // an "F1 — not answered" gap on every clean survey ever submitted.
  'F1',
  'L1',
];

/* ═══════════════════ the answer a question opens on (D-016) ═══════════════ */

/**
 * Answer kinds that have a natural "nothing here" value.
 *
 * The design authority, walkthrough round 3, 2026-08-06:
 *
 *   *"All answers should default to No (quantity 0) until the user selects Yes,
 *    at which point they must enter a quantity (if applicable). Many questions
 *    are not defaulted. I just closed and submitted an incomplete survey…
 *    I should not have been able to submit with unanswered questions."*
 *
 * Three kinds qualify and no others. `bool` and `flagCount` have an unambiguous
 * negative; `tristate` has an explicit **Unknown**, which is the honest opening
 * position for a question nobody has asked yet — see `defaultAnswerFor`.
 *
 * Everything else is deliberately absent. A `number` has no "no" (0 landings is
 * a claim, not a blank), a `select` has no null option, and `text`, `date`,
 * `contact`, `measure` and `ranges` are all empty-until-typed. Filling any of
 * those in would be the form inventing survey data.
 */
export const DEFAULTABLE_KINDS: readonly QuestionDef['kind'][] = ['bool', 'flagCount', 'tristate'];

/**
 * Questions of a defaultable kind that are nevertheless NOT defaulted, and the
 * reason each one is here. Each is a demonstrable defect rather than a
 * judgement call; every other substantive-claim question in §3 IS defaulted per the
 * design authority's instruction, and the list of those was escalated with this
 * change rather than settled here.
 *
 *  - **E4c1** — *"Do the disconnects you are refurbishing already have shunts?"*
 *    §4 rule 5 is the ONE validation that mutates the surveyor's answers, and it
 *    fires on an explicit `E4c1 = No`. `ruleShuntFlip` says so in as many words:
 *    *"Only an explicit 'no'. An unanswered follow-up is a gap, not a flip."*
 *    Defaulting it would rewrite E1a/E1b and raise a red `role="alert"` box the
 *    instant the question was revealed — the form changing the surveyor's
 *    answers over a question they had not been asked yet.
 *
 *  - **M4** — *"Is there a sprinkler in the machine room?"* It is not scope, it
 *    is a fact about the room, and it gates the whole E4 shunt block. A silent
 *    No would delete five questions from the Electrical section and record that
 *    the room has no sprinkler, neither of which anybody checked. M4 also lives
 *    on the room-setup screen, which the surveyor is walked through deliberately
 *    and which does not run the defaulting pass at all — so leaving it out keeps
 *    the catalog's claim and the app's behaviour the same claim.
 *
 *  - **G8a**, **O6a** — *"Is it still in use and has to stay?"* and **G9b** —
 *    *"Is a gate needed?"* (D-054). Each is a pricing answer the form would be
 *    inventing: a silent No on G8a/O6a says "abandoned", so in-use equipment
 *    goes out under Demo at $0, and a silent No on G9b prices a gated fence at
 *    the ungated per-foot rate ($50/ft instead of $100). Those are the silent
 *    mispricings Chip's answers were meant to prevent, so the surveyor must
 *    say Yes or No: revealed by the parent's Yes, each opens blank and is an
 *    unanswered gap until answered.
 *
 *  - **L9d** — *"Will barricades need to be moved to another elevator on this
 *    project?"* (D-058). A silent No drops 4 labor hours per opening on every
 *    later elevator the ICRA barricades move to — the same kind of silent
 *    mispricing as G8a/G9b/O6a. Revealed by L9a = ICRA, it opens blank and is
 *    an unanswered gap until the surveyor says Yes or No.
 */
export const NO_AUTO_DEFAULT_QUESTION_IDS: readonly string[] = ['E4c1', 'M4', 'G8a', 'G9b', 'O6a', 'L9d'];

/**
 * What a question opens on before the surveyor has touched it, or `undefined`
 * when it opens blank.
 *
 * This is the single statement of the rule; `src/ui/state/answerDefaults.ts`
 * applies it and nothing else decides it. Structural and fully-derived ids are
 * excluded here rather than at the call site because there is no such thing as
 * a default for them: M3 lives in the entity tree and B17 is counted, not
 * answered, and writing either into `answers` would corrupt the document.
 *
 * `tristate` defaults to **unknown**, not to no. §3.7's one tristate is L9c —
 * *"Has the facility said ALL floors must be barricaded?"* — and Unknown exists
 * precisely so a rare hospital edge case routes to the office instead of making
 * the surveyor guess. Recording `no` there would convert *"nobody asked"* into
 * *"the facility said no"*, which is a claim about a conversation that never
 * happened, and it would suppress the follow-up the specification wants.
 * Unknown is a real, complete answer — it clears the unanswered gap the design
 * authority hit — while asserting only what is true.
 */
export function defaultAnswerFor(question: QuestionDef): AnswerValue | undefined {
  if (STRUCTURAL_QUESTION_IDS.includes(question.id)) return undefined;
  if (DERIVED_ONLY_QUESTION_IDS.includes(question.id)) return undefined;
  if (NO_AUTO_DEFAULT_QUESTION_IDS.includes(question.id)) return undefined;

  switch (question.kind) {
    case 'bool':
      return { kind: 'bool', value: false };
    case 'flagCount':
      return { kind: 'flagCount', value: { flag: false, count: 0 } };
    case 'tristate':
      return { kind: 'tristate', value: 'unknown' };
    default:
      return undefined;
  }
}

/* ══════════════════════════════ §3.1 Building ══════════════════════════════ */

const BUILDING: QuestionDef[] = [
  {
    id: 'B1',
    level: 'building',
    section: 'building',
    label: 'Building name',
    hint: "What's on the sign out front.",
    kind: 'text',
    pricing: 'INFO',
    required: true,
    order: 1,
  },
  {
    id: 'B2',
    level: 'building',
    section: 'building',
    label: 'Street address',
    kind: 'text',
    pricing: 'INFO',
    required: true,
    order: 2,
  },
  // The address in four parts (D-039, 2026-09-04; required flipped by D-040,
  // 2026-09-05). Stephanie asked for city, state and ZIP as their own fields,
  // in the database as their own columns. Fractional orders seat them under
  // B2 without renumbering B3–B17. D-039 shipped them optional so a survey
  // already in flight would not gain a gap it did not have; she has now ruled
  // they are required, like B2 itself — an in-flight survey with B2 answered
  // and these blank now shows three gaps. Wherever one line is printed,
  // `formatAddress` (src/core/address.ts) composes it from B2 + these three —
  // an old record that holds everything in B2 prints exactly the string it
  // always did.
  {
    id: 'B2a',
    level: 'building',
    section: 'building',
    label: 'City',
    kind: 'text',
    pricing: 'INFO',
    required: true,
    order: 2.1,
  },
  {
    id: 'B2b',
    level: 'building',
    section: 'building',
    label: 'State',
    kind: 'text',
    pricing: 'INFO',
    required: true,
    order: 2.2,
  },
  {
    id: 'B2c',
    level: 'building',
    section: 'building',
    label: 'ZIP',
    kind: 'text',
    pricing: 'INFO',
    required: true,
    order: 2.3,
  },
  {
    id: 'B3',
    level: 'building',
    section: 'building',
    label: 'What kind of building is this?',
    // Hospital is called out in the spec because it makes ICRA barricades (L9a) likely.
    hint: 'Hospitals usually mean ICRA barricades later on.',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'hospital', label: 'Hospital / Healthcare' },
      { value: 'education', label: 'Education' },
      { value: 'commercial', label: 'Commercial / Office' },
      { value: 'residential', label: 'Residential / Multifamily' },
      { value: 'government', label: 'Government / Municipal' },
      { value: 'industrial', label: 'Industrial' },
      { value: 'hospitality', label: 'Hotel / Hospitality' },
      { value: 'other', label: 'Other' },
    ],
    required: true,
    order: 3,
  },
  {
    id: 'B4',
    level: 'building',
    section: 'building',
    label: 'Rise',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'high', label: 'High rise' },
      { value: 'mid', label: 'Mid rise' },
      { value: 'low', label: 'Low rise' },
    ],
    required: true,
    order: 4,
  },
  {
    id: 'B5',
    level: 'building',
    section: 'building',
    label: 'Building contact',
    hint: 'Whoever lets you in and answers questions on site.',
    kind: 'contact',
    pricing: 'INFO',
    order: 5,
  },
  {
    id: 'B6',
    level: 'building',
    section: 'building',
    // D-045 (Chip, 2026-09-11): was "Elevator company currently servicing the
    // building". The company this quote is for, not whoever holds the contract.
    label: 'Elevator company requesting quote',
    kind: 'text',
    pricing: 'INFO',
    order: 6,
  },
  {
    id: 'B7',
    level: 'building',
    section: 'building',
    label: 'Elevator sales rep',
    kind: 'contact',
    pricing: 'INFO',
    order: 7,
  },
  {
    id: 'B7a',
    level: 'building',
    section: 'building',
    label: 'Survey due date',
    kind: 'date',
    pricing: 'INFO',
    order: 7.5,
  },
  {
    id: 'B8',
    level: 'building',
    section: 'building',
    label: 'Is this a consultant bid?',
    // B8 is a two-way gate: consultant contact + due date here, and the
    // upper-landing wall-type question (L4) over in Lobbies.
    hint: 'Yes opens the consultant details here and the per-landing wall types in Lobbies.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 8,
  },
  {
    id: 'B9',
    level: 'building',
    section: 'building',
    label: 'Consultant',
    kind: 'contact',
    pricing: 'INFO',
    showWhen: [{ questionId: 'B8', equals: true }],
    order: 9,
  },
  {
    id: 'B10',
    level: 'building',
    section: 'building',
    label: 'Bid due date',
    kind: 'date',
    pricing: 'INFO',
    // Spec §4 rule 9: a consultant bid without a due date is a gap.
    showWhen: [{ questionId: 'B8', equals: true }],
    required: true,
    order: 10,
  },
  {
    id: 'B11',
    level: 'building',
    section: 'building',
    label: 'Total elevators in the building',
    hint: 'Every car, all machine rooms. The form checks this against the cars you assign room by room.',
    kind: 'number',
    pricing: 'INFO',
    uom: 'car',
    required: true,
    order: 11,
  },
  {
    id: 'B12',
    level: 'building',
    section: 'building',
    label: 'How many machine rooms?',
    hint: 'One room survey per machine room.',
    kind: 'number',
    pricing: 'INFO',
    uom: 'room',
    required: true,
    // D-054: a cap. Every number is typeable since D-053, and each room is a
    // slot on the map, so a slip like "2000" would lay out 2000 rows and stall
    // the phone. A typed number over 10 is clamped to 10 in the box.
    max: 10,
    order: 12,
  },
  {
    id: 'B13',
    level: 'building',
    section: 'building',
    label: 'Is the building on emergency power?',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 13,
  },
  {
    id: 'B14',
    level: 'building',
    section: 'building',
    label: 'Cars on the ATS',
    // DECISIONS D-003 (approved amendment): ATS membership is a per-car fact
    // (CarRef.onAts), so this count is DERIVED, never typed — the same treatment
    // Q4 already gives room unit counts. A typed number here could contradict
    // the car tags and nothing would catch it.
    hint: 'Counted for you from the cars you tag "on ATS" in each machine room.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'car',
    showWhen: [{ questionId: 'B13', equals: true }],
    auto: true,
    order: 14,
  },
  {
    id: 'B15',
    level: 'building',
    section: 'building',
    label: 'Where is the ATS?',
    kind: 'text',
    pricing: 'INFO',
    showWhen: [{ questionId: 'B13', equals: true }],
    order: 15,
  },
  {
    id: 'B16',
    level: 'building',
    section: 'building',
    label: 'Is the ATS programmable?',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'B13', equals: true }],
    order: 16,
  },
  {
    id: 'B17',
    level: 'building',
    section: 'building',
    label: 'ATS pre/post signaling',
    // Q26: priced per machine room, not per car. Quantity comes from CarRef.onAts
    // rolled up to rooms — the line reads "ATS – X cars in Y Machine Rooms."
    hint: 'Counted for you — one per machine room that has ATS cars in it.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'room',
    showWhen: [{ questionId: 'B13', equals: true }],
    auto: true,
    order: 17,
  },
];

/* ═════════════════════════ §3.2 Machine Room Setup ═════════════════════════ */

const ROOM_SETUP: QuestionDef[] = [
  {
    id: 'M1',
    level: 'room',
    section: 'room_setup',
    label: 'Room type',
    hint: 'Answer this first — it decides which questions you get. MRL runs the Hydro set, tagged MRL.',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'traction', label: 'Traction' },
      { value: 'hydro', label: 'Hydro' },
      { value: 'mrl', label: 'MRL', hint: 'MRL conversion — you fill out the Hydro question set.' },
    ],
    required: true,
    order: 1,
  },
  {
    id: 'M2',
    level: 'room',
    section: 'room_setup',
    label: 'Name this machine room',
    hint: 'Whatever you would call it on site — "Penthouse Machine Room".',
    kind: 'text',
    pricing: 'INFO',
    required: true,
    order: 2,
  },
  {
    id: 'M3',
    level: 'room',
    section: 'room_setup',
    label: 'Cars this room serves',
    // Q4: the unit count is derived from this list and never typed. It also feeds
    // the B11 building cross-check (spec §4 rule 1).
    hint: 'Add every car. The unit count is counted for you and checked against the building total.',
    kind: 'multiselect',
    pricing: 'AUTO',
    uom: 'car',
    auto: true,
    required: true,
    order: 3,
  },
  {
    id: 'M4',
    level: 'room',
    section: 'room_setup',
    label: 'Is there a sprinkler in the machine room?',
    // Q13: this is the ONLY thing that opens the E4 shunt questions.
    hint: 'Yes opens the shunt questions in Electrical.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 4,
  },
  {
    id: 'M5',
    level: 'room',
    section: 'room_setup',
    label: 'Where is the machine room?',
    // Q41: context only. The valid choices differ by form set, so each option
    // carries its own formSets tag (DECISIONS D-007) and callers filter on it.
    hint: 'Context for the office — no price attached.',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'overhead', label: 'Overhead', formSets: ['traction'] },
      { value: 'basement', label: 'Basement', formSets: ['traction'] },
      { value: 'adjacent_lowest', label: 'Adjacent — lowest landing', formSets: ['hydro'] },
      { value: 'adjacent_top', label: 'Adjacent — top landing', formSets: ['hydro'] },
      { value: 'remote', label: 'Remote', formSets: ['hydro'] },
    ],
    order: 5,
  },
  {
    id: 'M6',
    level: 'room',
    section: 'room_setup',
    label: 'Groups — banks of cars with their own riser and hall calls',
    // Q5–6: landings are captured per group because cars in one room can serve
    // different landings. Lobby sections are generated one per group.
    hint: 'Name each group, tap its cars in, and set the landings it serves. Lobby questions repeat once per group. 1 landing ≈ 9 ft.',
    kind: 'multiselect',
    pricing: 'INFO',
    required: true,
    order: 6,
  },
  {
    id: 'M7',
    level: 'room',
    section: 'room_setup',
    label: 'Hoistways — which cars share a shaft',
    // Q7–8: overhead and pit sections are generated one per hoistway.
    hint: 'Overhead and pit questions repeat once per hoistway.',
    kind: 'multiselect',
    pricing: 'INFO',
    required: true,
    order: 7,
  },
];

/* ═══════════════════════════ §3.3 Electrical ═══════════════════════════════ */

const ELECTRICAL: QuestionDef[] = [
  {
    id: 'E1a',
    level: 'room',
    section: 'electrical',
    label: 'Are any mainline disconnects being replaced with new ones?',
    // D-015: the control is Yes/No, so the QUESTION is Yes/No. The quantity is
    // a follow-up that appears after Yes, and asks "How many?" in its own words.
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 1,
  },
  {
    id: 'E1b',
    level: 'room',
    section: 'electrical',
    label: 'Are any mainline disconnects being kept and refurbished?',
    // Q18–19 / §4 rule 2: exact equality with the room car count. "Leave as-is"
    // is not an acceptable disposition for a disconnect.
    hint: 'Replaced + refurbished has to add up to the car count. Leaving one as-is is not allowed on disconnects.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 2,
  },
  {
    id: 'E2',
    level: 'room',
    section: 'electrical',
    label: 'Is any mainline surge protection going in?',
    hint: 'Say Yes and the form asks how many. It can never be more than the car count.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 3,
  },
  {
    id: 'E3',
    level: 'room',
    section: 'electrical',
    label: 'Do any disconnects need relocating?',
    hint: 'Say Yes and the form asks how many. It can never be more than the car count.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 4,
  },
  {
    id: 'E4a',
    level: 'room',
    section: 'electrical',
    label: 'Sprinkler in the room — where do the shunts go?',
    // Q13: the whole E4 block exists only because M4 (sprinkler) = Y.
    kind: 'select',
    pricing: 'INFO',
    options: [
      {
        value: 'external',
        label: 'Shunt the room externally',
        hint: 'One shunt for the whole room — far cheaper.',
      },
      {
        value: 'in_disconnect',
        label: 'Shunts go inside the disconnects',
        hint: 'Every disconnect has to carry one.',
      },
    ],
    showWhen: [{ questionId: 'M4', equals: true }],
    required: true,
    order: 5,
  },
  {
    id: 'E4b',
    level: 'room',
    section: 'electrical',
    label: 'External room shunt',
    hint: 'One per room. Counted for you.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'room',
    showWhen: [
      { questionId: 'M4', equals: true },
      { questionId: 'E4a', equals: 'external' },
    ],
    auto: true,
    order: 6,
  },
  {
    id: 'E4c1',
    level: 'room',
    section: 'electrical',
    label: 'Do the disconnects you are refurbishing already have shunts?',
    // Q13 rule / §4 rule 5. The spec describes this follow-up inside the E4c row
    // but never assigns it an ID; it needs its own key because the auto-fix
    // (shunt_refurb_to_replace) triggers off it. It prices nothing — the flipped
    // quantities land on E1a/E1b — so its ID carries no catalog line. Only asked
    // when something was actually marked refurbish (E1b).
    //
    // NOTE: `E4c1` is the ONLY id in the catalog with a trailing digit, and
    // AnswerKeySchema in schema.ts carries an `\d?` for it. Anything that parses
    // question ids must accept the shape; do not "tidy" it to E4d — that would be
    // a rename of a key already written into stored documents.
    hint: 'A disconnect can only be refurbished if it already has a shunt. If not, the form moves them to replace and tells you why.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [
      { questionId: 'M4', equals: true },
      { questionId: 'E4a', equals: 'in_disconnect' },
      { questionId: 'E1b', flagIs: true },
    ],
    required: true,
    order: 7,
  },
  {
    id: 'E4c',
    level: 'room',
    section: 'electrical',
    label: 'New mainline disconnects with shunt',
    // HCA Tallahassee 10/30/26 (D-057): the count is the replaced disconnects,
    // not the car count — a refurbished one keeps the shunt it already has.
    hint: 'One for each disconnect being replaced. Refurbished ones keep the shunt they already have. Counted for you.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'ea',
    showWhen: [
      { questionId: 'M4', equals: true },
      { questionId: 'E4a', equals: 'in_disconnect' },
    ],
    auto: true,
    order: 8,
  },
  {
    id: 'E5a',
    level: 'room',
    section: 'electrical',
    label: 'Are any cab light 110 disconnects being replaced with new ones?',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 9,
  },
  {
    id: 'E5b',
    level: 'room',
    section: 'electrical',
    label: 'Are any cab light 110 disconnects being kept and refurbished?',
    // §4 rule 3: its own exact-equality check, independent of the mainline split.
    // D-053 (Chip, 2026-10-27): "Cab light disconnects = 1 per car". The hint
    // used to say "every car has two disconnects" — the mainline and this one
    // together — which read as two cab light disconnects per car. The rule was
    // always replaced + refurbished = cars, which IS one per car.
    hint: 'Separate from the mainline — one cab light disconnect per car. Replaced + refurbished has to add up to the car count.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 10,
  },
  {
    id: 'E6',
    level: 'room',
    section: 'electrical',
    label: 'Is a 2019 Emergency Communications 110 disconnect needed?',
    // Q14: the Y/N is the surveyor's; the quantity is ceil(cars ÷ 3).
    hint: 'Say yes or no — the count is one per three cars, rounded up. 6 cars = 2, 7 cars = 3.',
    kind: 'bool',
    pricing: 'AUTO',
    uom: 'ea',
    auto: true,
    required: true,
    order: 11,
  },
  {
    id: 'E6a',
    level: 'room',
    section: 'electrical',
    label: 'CAT6 runs',
    hint: 'One run to each car. Added automatically when the 2019 disconnect is needed.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'ea',
    showWhen: [{ questionId: 'E6', equals: true }],
    auto: true,
    order: 12,
  },
  {
    id: 'E7',
    level: 'room',
    section: 'electrical',
    label: 'Are the elevators tied into a lobby panel?',
    // Q15: one panel per building (usually the fire/security room), but the
    // conduit runs from each machine room, so it is asked per room.
    hint: 'About 1 in 20 buildings. Usually the fire/security room. One panel per building, but conduit runs from every room — so answer it in each one.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 13,
  },
  {
    id: 'E7a',
    level: 'room',
    section: 'electrical',
    label: 'Can the existing conduit be reused?',
    hint: 'Reuse is a fish-and-pull — effectively no cost, and no conduit questions.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'E7', equals: true }],
    required: true,
    order: 14,
  },
  {
    id: 'E7b',
    level: 'room',
    section: 'electrical',
    label: 'New conduit — vertical leg: how many floors from this machine room to the ground?',
    // Q16: one punch per floor, run near the hoistway.
    hint: 'One punch per floor, near the hoistway. Photograph the punch path.',
    kind: 'number',
    pricing: 'SURV-QTY',
    uom: 'floor',
    showWhen: [
      { questionId: 'E7', equals: true },
      { questionId: 'E7a', equals: false },
    ],
    required: true,
    order: 15,
  },
  {
    id: 'E7c',
    level: 'room',
    section: 'electrical',
    label: 'New conduit — horizontal leg: how far, in feet?',
    // Q16: you supply feet; the quantity is ceil(feet ÷ 25).
    hint: 'Pace it off. Priced in 25-ft units, rounded up — 60 ft is 3 units.',
    kind: 'measure',
    pricing: 'AUTO',
    uom: '25-ft unit',
    showWhen: [
      { questionId: 'E7', equals: true },
      { questionId: 'E7a', equals: false },
    ],
    auto: true,
    required: true,
    order: 16,
  },
  {
    id: 'E8',
    level: 'room',
    section: 'electrical',
    label: 'Convenience GFCI + non-GFCI outlets needed?',
    hint: 'Say yes or no — the count is one per car. Counted for you.',
    kind: 'bool',
    pricing: 'AUTO',
    uom: 'ea',
    auto: true,
    required: true,
    order: 17,
  },
  {
    id: 'E9a',
    level: 'room',
    section: 'electrical',
    // D-045 (Chip, 2026-09-11): was "How many light fixtures are in the room
    // now?" — the count that EXISTS. Chip asks for the count NEEDED instead, so
    // E9d (the priced LED line) is this number alone (calc.ts).
    label: 'How many guarded LED lighting fixtures are needed?',
    kind: 'number',
    pricing: 'INFO',
    uom: 'fixture',
    required: true,
    order: 18,
  },
  // E9b "Is the existing lighting adequate?" (bool, order 19) and E9c "How many
  // additional fixtures are needed?" (number behind E9b = No, order 20) were
  // DELETED by Chip on 2026-09-11 (D-046): with E9a asking for the fixtures
  // needed, both were redundant. Ids not reused; stored answers stay inert.
  {
    id: 'E9d',
    level: 'room',
    section: 'electrical',
    label: 'New LED strip fixtures',
    // Q17 as amended by D-045/D-046: the quantity is E9a, the fixtures needed.
    hint: 'The fixtures needed. Counted for you. If you have to guess, about one fixture per 8 sq ft.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'fixture',
    auto: true,
    order: 21,
  },
  {
    id: 'E10',
    level: 'room',
    section: 'electrical',
    label: 'New emergency lighting?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 22,
  },
  {
    id: 'E11',
    level: 'room',
    section: 'electrical',
    label: '"Always on" 3–5W LED?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 23,
  },
  {
    id: 'E12',
    level: 'room',
    section: 'electrical',
    label: 'Move the light switch to the entrance?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 24,
  },
  {
    id: 'E13',
    level: 'room',
    section: 'electrical',
    label: 'Are any phone lines being run to the controller?',
    hint: 'Say Yes and the form asks how many. Counted per car.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'car',
    required: true,
    order: 25,
  },
  {
    id: 'E14',
    level: 'room',
    section: 'electrical',
    label: 'Is an auxiliary contact for battery lowering needed?',
    hint: 'Say Yes and the form asks how many. Counted per car.',
    // Q29: hydro only. MRL rooms get it too, since MRL runs the Hydro set.
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'car',
    formSets: ['hydro'],
    required: true,
    order: 26,
  },
];

/* ═════════════════════════════ §3.4 HVAC ══════════════════════════════════ */

const HVAC: QuestionDef[] = [
  {
    id: 'H1',
    level: 'room',
    section: 'hvac',
    label: 'Does this room need HVAC?',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 1,
  },
  {
    id: 'H1a',
    level: 'room',
    section: 'hvac',
    label: 'Mini-split or chilled water?',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'minisplit', label: 'Mini-split' },
      { value: 'chilled', label: 'Chilled water' },
    ],
    showWhen: [{ questionId: 'H1', equals: true }],
    required: true,
    order: 2,
  },
  {
    id: 'H2',
    level: 'room',
    section: 'hvac',
    label: 'Room dimensions — length × width',
    // Q20–21: the office sizes the unit from these numbers. Do not let the
    // surveyor pick tonnage.
    hint: 'The office picks the tonnage from these numbers — do not size the unit yourself.',
    kind: 'measure',
    pricing: 'OFFICE',
    uom: 'ft',
    showWhen: [
      { questionId: 'H1', equals: true },
      { questionId: 'H1a', equals: 'minisplit' },
    ],
    required: true,
    order: 3,
  },
  {
    id: 'H3',
    level: 'room',
    section: 'hvac',
    // D-053 (Chip, 2026-10-27): was "Condensate line — …". He calls it the
    // refrigerant line; the id, the feet and the 25-ft units are unchanged
    // (calc.ts keeps its `condensate*` names — they are code, not words).
    label: 'Refrigerant line — how many feet is the run?',
    // Standard price covers up to 100 ft; beyond that, ceil((feet − 100) ÷ 25)
    // extra units. 130 ft = standard + 2.
    hint: 'Standard price covers up to 100 ft. Past that it is one more unit per 25 ft — 130 ft is standard + 2. Note the routing and photograph it.',
    kind: 'measure',
    pricing: 'AUTO',
    uom: '25-ft unit',
    showWhen: [
      { questionId: 'H1', equals: true },
      { questionId: 'H1a', equals: 'minisplit' },
    ],
    // Same shape as E7c: the surveyor supplies feet, the engine supplies the
    // unit count. Flagged auto so both measured-distance questions read alike.
    auto: true,
    required: true,
    order: 4,
  },
  {
    id: 'H4',
    level: 'room',
    section: 'hvac',
    label: 'Chilled water details',
    // §4 rule 10: this forces a mandatory manual line into the estimate skeleton
    // so it cannot be quietly dropped.
    hint: 'Write down what you see and take good photos. The office prices this by hand — it cannot be dropped from the estimate.',
    kind: 'text',
    pricing: 'OFFICE',
    showWhen: [
      { questionId: 'H1', equals: true },
      { questionId: 'H1a', equals: 'chilled' },
    ],
    required: true,
    order: 5,
  },
  {
    id: 'H5',
    level: 'room',
    section: 'hvac',
    // Asked whether or not H1 is yes — it is about equipment already in the room.
    label: 'Does the existing HVAC need a thermostat?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 6,
  },
  {
    id: 'H6',
    level: 'room',
    section: 'hvac',
    label: 'Service the existing HVAC?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 7,
  },
];

/* ════════════════════════════ §3.5 General ════════════════════════════════ */

const GENERAL: QuestionDef[] = [
  {
    id: 'G1',
    level: 'room',
    section: 'general',
    label: 'New 90-minute fire rated door?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 1,
  },
  {
    id: 'G2',
    level: 'room',
    section: 'general',
    label: 'Does the door need new hardware?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 2,
  },
  {
    id: 'G3',
    level: 'room',
    section: 'general',
    label: 'Is core drilling required?',
    // D-059: priced per hole. A Yes/No hid "about 4 holes" and HCA nearly went out at 8 x the room price.
    hint: 'Say Yes and the form asks how many holes. Count every hole in this room.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'hole',
    required: true,
    order: 3,
  },
  {
    id: 'G4',
    level: 'room',
    section: 'general',
    label: 'Fire caulking required?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 4,
  },
  {
    id: 'G5',
    level: 'room',
    section: 'general',
    label: 'Penetrations to seal?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 5,
  },
  {
    id: 'G6',
    level: 'room',
    section: 'general',
    label: 'Vents, louvers, or windows to cover in sheetmetal?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 6,
  },
  {
    id: 'G7',
    level: 'room',
    section: 'general',
    label: 'Paint the machine room walls?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 7,
  },
  {
    id: 'G8',
    level: 'room',
    section: 'general',
    label: 'Non-elevator equipment or wiring to move or remove?',
    // Q22: out-of-box situations ride out on notes + photos to the office.
    hint: 'Say what it is and where it goes in the notes, and photograph it.',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 8,
  },
  {
    id: 'G8a',
    level: 'room',
    section: 'general',
    // D-054 (Chip, 2026-10-27): asked only when G8 is Yes. Abandoned equipment
    // goes out with the demo; equipment still in use that has to stay is
    // priced by the estimator. INFO here, like A2 — the pricing catalog, not
    // this file, holds the price.
    label: 'Is it still in use and has to stay?',
    hint: 'Abandoned equipment is covered by Demo. If it is in use and has to stay, the estimator prices it.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'G8', equals: true }],
    required: true,
    order: 8.5,
  },
  {
    id: 'G9',
    level: 'room',
    section: 'general',
    label: 'Chain-link fence to separate the equipment?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 9,
  },
  {
    id: 'G9a',
    level: 'room',
    section: 'general',
    // D-054 (Chip, 2026-10-27): the fence is priced by the foot, so the run is
    // asked when G9 is Yes. INFO here, like A2 — the pricing catalog holds the
    // per-foot price. At least 1: a fence of 0 ft is G9 = No.
    label: 'How many feet of fence?',
    kind: 'number',
    pricing: 'INFO',
    uom: 'ft',
    showWhen: [{ questionId: 'G9', equals: true }],
    required: true,
    min: 1,
    order: 9.1,
  },
  {
    id: 'G9b',
    level: 'room',
    section: 'general',
    // D-054 (Chip, 2026-10-27): whether the fence needs a gate, asked with
    // G9a. INFO here, like A2 — the pricing catalog holds the price.
    label: 'Is a gate needed?',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'G9', equals: true }],
    required: true,
    order: 9.2,
  },
  {
    id: 'G10',
    level: 'room',
    section: 'general',
    label: 'Exposed pipes that need pans underneath?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 10,
  },
  {
    id: 'G11',
    level: 'room',
    section: 'general',
    label: 'Do any beams need to be rated for moving equipment?',
    // Q30: an engineer certifies the load and the price swings by market, so the
    // catalog carries market pricing against this per-beam count.
    hint: 'An engineer certifies the load. Price varies by market — say Yes and the form asks how many beams.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'beam',
    formSets: ['traction'],
    required: true,
    order: 11,
  },
  {
    id: 'G12',
    level: 'room',
    section: 'general',
    label: 'I-beams to INSTALL for equipment moves?',
    // Q30: informational flag only — the office works up the price from photos.
    hint: 'Flag it and take good photos — the office works up the price.',
    kind: 'bool',
    pricing: 'OFFICE',
    formSets: ['traction'],
    required: true,
    order: 12,
  },
  {
    id: 'G13',
    level: 'room',
    section: 'general',
    label: 'Secondary railing removed and reinstalled?',
    kind: 'bool',
    pricing: 'FLAT',
    formSets: ['traction'],
    required: true,
    order: 13,
  },
  {
    id: 'G14',
    level: 'room',
    section: 'general',
    label: 'Does the secondary need new railing?',
    kind: 'bool',
    pricing: 'FLAT',
    formSets: ['traction'],
    required: true,
    order: 14,
  },
  {
    id: 'G14a',
    level: 'room',
    section: 'general',
    // D-054 (Chip, 2026-10-27): new railing is priced by the foot. Traction
    // only, as G14 is. INFO here, like A2 — the pricing catalog holds the
    // per-foot price. At least 1: no railing is G14 = No.
    label: 'How many feet of railing?',
    kind: 'number',
    pricing: 'INFO',
    uom: 'ft',
    formSets: ['traction'],
    showWhen: [{ questionId: 'G14', equals: true }],
    required: true,
    min: 1,
    order: 14.5,
  },
  {
    id: 'G15',
    level: 'room',
    section: 'general',
    label: 'Does the secondary need new stairs?',
    kind: 'bool',
    pricing: 'FLAT',
    formSets: ['traction'],
    required: true,
    order: 15,
  },
  {
    id: 'G16',
    level: 'room',
    section: 'general',
    label: 'Rooftop access — does the entrance need new railing or a platform?',
    kind: 'bool',
    pricing: 'FLAT',
    formSets: ['traction'],
    required: true,
    order: 16,
  },
  {
    id: 'G17',
    level: 'room',
    section: 'general',
    label: 'Entrance widened to get equipment in?',
    kind: 'bool',
    pricing: 'FLAT',
    formSets: ['traction'],
    required: true,
    order: 17,
  },
  {
    id: 'F15',
    level: 'room',
    section: 'general',
    // Amendment 8 (2026-09-06, D-041). The fire alarm company's form has a box
    // for it and their own report listed "no measured figure" as an open item.
    // Asked in General, beside the wide room shot, not in Room Setup (D-030
    // cut that screen to nine structural items). Feet, because that is what
    // the company writes on the paperwork; 0 is "the panel is in this room".
    label: 'How far is the fire alarm panel from this machine room?',
    hint: 'Pace it off, in feet. 0 means the panel is in this room.',
    kind: 'number',
    pricing: 'INFO',
    uom: 'ft',
    required: true,
    order: 18,
  },
];

/* ═══════════════════ §3.6 Overhead / Hoistway (per hoistway) ═══════════════ */

const OVERHEAD: QuestionDef[] = [
  {
    id: 'O1',
    level: 'hoistway',
    section: 'overhead',
    label: 'Vents or louvers to cover?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 1,
  },
  {
    id: 'O2',
    level: 'hoistway',
    section: 'overhead',
    label: 'Smoke detectors at the top of the hoistway?',
    // Q32: recorded only — another trade acts on it.
    hint: 'Recorded for the file. Another trade handles these.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 2,
  },
  {
    id: 'O3',
    level: 'hoistway',
    section: 'overhead',
    label: 'Sprinklers or heat detectors at the top?',
    hint: 'Recorded for the file. Another trade handles these.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 3,
  },
  {
    id: 'O4',
    level: 'hoistway',
    section: 'overhead',
    label: 'Are the hoistways separated or screened?',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 4,
  },
  {
    id: 'O5',
    level: 'hoistway',
    section: 'overhead',
    label: 'Are there any ledges that need beveling?',
    hint: 'Say Yes and the form asks how many.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 5,
  },
  {
    id: 'O6',
    level: 'hoistway',
    section: 'overhead',
    label: 'Non-elevator equipment to remove or gutter?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 6,
  },
  {
    id: 'O6a',
    level: 'hoistway',
    section: 'overhead',
    // D-054 (Chip, 2026-10-27): G8a's question, for the hoistway. Asked only
    // when O6 is Yes. INFO here, like A2 — the pricing catalog holds the price.
    label: 'Is it still in use and has to stay?',
    hint: 'Abandoned equipment is covered by Demo. If it is in use and has to stay, the estimator prices it.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'O6', equals: true }],
    required: true,
    order: 6.5,
  },
  {
    id: 'O7',
    level: 'hoistway',
    section: 'overhead',
    label: 'Penetrations to seal?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 7,
  },
  {
    id: 'O8',
    level: 'hoistway',
    section: 'overhead',
    label: 'Fire caulking required?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 8,
  },
  {
    id: 'O9',
    level: 'hoistway',
    section: 'overhead',
    label: 'Does any sheetrock need repairing?',
    hint: 'Say Yes and the form asks how many. Counted per sheet.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'sheet',
    required: true,
    order: 9,
  },
];

/* ═════════════════════ §3.7 Lobbies (per group) ═══════════════════════════ */

const LOBBY: QuestionDef[] = [
  {
    id: 'L1',
    level: 'group',
    section: 'lobby',
    label: 'Landings this group serves',
    // Q9–12: taken from the group definition (M6), never re-asked.
    hint: 'Taken from the group you set up — nothing to enter.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'landing',
    auto: true,
    order: 1,
  },
  {
    id: 'L2',
    level: 'group',
    section: 'lobby',
    label: 'Main Egress wall type',
    // Q10–11: always captured. The material selects the unit price for the Main
    // Egress cut & patch, so it is never optional even on non-consultant jobs.
    hint: 'Always capture this. Price swings a lot between materials.',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'sheetrock', label: 'Sheetrock' },
      { value: 'block', label: 'Block' },
      { value: 'brick', label: 'Brick' },
      { value: 'cement', label: 'Cement' },
      { value: 'tile', label: 'Tile' },
      { value: 'marble', label: 'Marble' },
      // Amendment 5, 2026-08-07. This list is also the L4a per-landing picker —
      // RangeField reads L2's options rather than restating them — so adding it
      // here reaches both places at once.
      { value: 'stucco', label: 'Stucco' },
    ],
    required: true,
    order: 2,
  },
  // L3 — "Are any fixtures going on the Main Egress lobby?" (a flagCount,
  // order 3; reworded to a count by D-045) — was DELETED by Chip on 2026-09-11
  // (D-046): OSB does not supply the fixtures, only the cuts and patches for
  // the elevator contractor's, and the word "fixtures" on the survey read as
  // if OSB did. The id is not reused; stored answers stay inert. L3a below is
  // the one count this lobby is asked for.
  {
    id: 'L3a',
    level: 'group',
    section: 'lobby',
    // D-045 (Chip, 2026-09-11): added after L3 — fractional order, the D-034
    // pattern, so L4 onward keep their numbers. D-046 (Chip, the same day):
    // this is THE count the Main Egress cut & patch rows are priced off —
    // "one hall call station and two position indicators" is 3 cut/patch —
    // chosen by the group's L2 wall type. No hint (R1): the label carries it.
    label: 'How many cut/patch needed?',
    kind: 'number',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 3.5,
  },
  {
    id: 'L4',
    level: 'group',
    section: 'lobby',
    label: 'Does the spec call for fixture cutting and patching beyond the Main Egress?',
    // Q11: really only happens on consultant jobs, so B8 gates the question and
    // L4 itself gates the per-landing range entry.
    hint: 'Consultant jobs usually do. If no, you are done with wall types.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'B8', equals: true }],
    required: true,
    order: 4,
  },
  {
    id: 'L4a',
    level: 'group',
    section: 'lobby',
    label: 'Wall type per landing',
    // Q11: range entry so nobody taps 19 landings one at a time.
    hint: 'Enter ranges — "2–20 sheetrock" — instead of tapping every landing.',
    kind: 'ranges',
    pricing: 'SURV-QTY',
    uom: 'landing',
    showWhen: [{ questionId: 'L4', equals: true }],
    required: true,
    order: 5,
  },
  {
    id: 'L5',
    level: 'group',
    section: 'lobby',
    label: 'Smoke or heat detectors present?',
    // Q32: recorded only — another trade acts on it.
    hint: 'Recorded for the file. Another trade handles these.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 6,
  },
  {
    id: 'L6',
    level: 'group',
    section: 'lobby',
    label: 'Grout any sills?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 7,
  },
  {
    id: 'L7',
    level: 'group',
    section: 'lobby',
    label: 'Are any entrances or hoistway doors being painted?',
    hint: 'Say Yes and the form asks how many. Counted per door.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'door',
    required: true,
    order: 8,
  },
  {
    id: 'L8',
    level: 'group',
    section: 'lobby',
    label: 'New riser needed?',
    // Q12: rare. Quantity is the group's landing count, priced per floor. The one
    // line bundles new conduit run + back boxes + cutting/patching. Max 1 per group.
    hint: 'Rare. Say yes or no — the count is this group\'s landings, priced per floor. One line covers conduit, back boxes, and cut & patch. One riser per group, max.',
    kind: 'bool',
    pricing: 'AUTO',
    uom: 'landing',
    auto: true,
    required: true,
    order: 9,
  },
  {
    id: 'L9',
    level: 'group',
    section: 'lobby',
    label: 'Barricades needed?',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 10,
  },
  {
    id: 'L9a',
    level: 'group',
    section: 'lobby',
    label: 'Barricade type',
    // Q31. ICRA barricades are OSB-owned and rented to the job. Nothing here
    // prices a move: D-058 asks it below (L9d, L9e, L9f) and the pricing catalog
    // holds the 4 MH per opening on each later elevator.
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'general', label: 'General' },
      { value: 'icra', label: 'ICRA', hint: 'OSB-owned, rented to the job. Moving them is asked below.' },
    ],
    showWhen: [{ questionId: 'L9', equals: true }],
    required: true,
    order: 11,
  },
  {
    id: 'L9b',
    level: 'group',
    section: 'lobby',
    label: 'How many landings need barricades?',
    hint: 'Count the landings where work actually happens.',
    kind: 'number',
    pricing: 'SURV-QTY',
    uom: 'landing',
    showWhen: [{ questionId: 'L9', equals: true }],
    required: true,
    order: 12,
  },
  {
    id: 'L9c',
    level: 'group',
    section: 'lobby',
    label: 'Has the facility said ALL floors must be barricaded?',
    // Q31: guards the rare hospital edge case. Unknown routes to office follow-up
    // rather than forcing the surveyor to guess.
    hint: 'If you do not know, say Unknown — the office will follow up.',
    kind: 'tristate',
    pricing: 'INFO',
    showWhen: [{ questionId: 'L9', equals: true }],
    required: true,
    order: 13,
  },
  {
    id: 'L9d',
    level: 'group',
    section: 'lobby',
    // D-058 (Chip, 2026-10-30, Thomas approved): ICRA barricades that come down
    // and go back up on another elevator cost 4 labor hours per opening there.
    // Asked only for ICRA; L9a is itself asked only under L9 = Yes, and a gate
    // whose question is not asked is closed (validation.ts conditionHolds), so
    // L9 = No closes this too. INFO, like A2 — the pricing catalog holds the
    // price. Exempt from the No default (NO_AUTO_DEFAULT_QUESTION_IDS).
    label: 'Will barricades need to be moved to another elevator on this project?',
    hint: 'Say Yes if the same barricades come down and go back up on another elevator.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'L9a', equals: 'icra' }],
    required: true,
    order: 13.1,
  },
  {
    id: 'L9f',
    level: 'group',
    section: 'lobby',
    // D-058 (Stephanie, 2026-10-30): the barricades are rented once for the whole
    // move, sized by how many cars are open at once — barricades rented =
    // landings x this number. Asked before L9e, which counts the elevators after these (Stephanie 2026-11-02). INFO; the pricing catalog does
    // the sum. At least 1, at most 50 (a typed-count cap like L9e's).
    label: 'How many cars are worked on at the same time?',
    hint: 'The barricades rented = landings x this number.',
    kind: 'number',
    pricing: 'INFO',
    uom: 'car',
    showWhen: [{ questionId: 'L9d', equals: true }],
    required: true,
    min: 1,
    max: 50,
    order: 13.2,
  },
  {
    id: 'L9e',
    level: 'group',
    section: 'lobby',
    // D-058: each later elevator is 4 MH per opening in the pricing catalog;
    // the first cars (L9f) stay in the rental line. At least 1: none is L9d = No. At
    // most 50, a typed-count cap like B12's (D-054). uom 'car', the catalog's
    // existing word for an elevator (B11, E13, E14).
    label: 'After those first cars, how many more elevators will they move to?',
    hint: "Don't count the cars worked on first.",
    kind: 'number',
    pricing: 'INFO',
    uom: 'car',
    showWhen: [{ questionId: 'L9d', equals: true }],
    required: true,
    min: 1,
    max: 50,
    order: 13.3,
  },
];

/* ═══════════════════════ §3.8 Pit (per hoistway) ══════════════════════════ */

const PIT: QuestionDef[] = [
  {
    id: 'P1',
    level: 'hoistway',
    section: 'pit',
    label: 'Does the pit need a sump pump?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 1,
  },
  {
    id: 'P2',
    level: 'hoistway',
    section: 'pit',
    // Q23 explicit callout: P2/P3/P4 are ALWAYS SHOWN, even when P1 = N. An
    // existing pump may still need a grate, a basket cut-out, or copper
    // discharge. Do not add a showWhen here.
    label: 'Does the sump pump need a grate?',
    hint: 'Asked whether or not you are adding a pump — an existing pump may still need one.',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 2,
  },
  {
    id: 'P3',
    level: 'hoistway',
    section: 'pit',
    // Q23: always shown — see P2.
    label: 'Basket cut-out for the sump pump?',
    hint: 'Asked whether or not you are adding a pump.',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 3,
  },
  {
    id: 'P4',
    level: 'hoistway',
    section: 'pit',
    // Q23: always shown — see P2.
    label: 'Copper discharge?',
    hint: 'Asked whether or not you are adding a pump.',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 4,
  },
  {
    id: 'P5',
    level: 'hoistway',
    section: 'pit',
    label: 'Conduit raised to meet code?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 5,
  },
  {
    id: 'P6',
    level: 'hoistway',
    section: 'pit',
    label: 'Oil Minder?',
    // Q29: hydro only, priced per pit like the other sump items.
    kind: 'bool',
    pricing: 'FLAT',
    formSets: ['hydro'],
    required: true,
    order: 6,
  },
  {
    id: 'P7',
    level: 'hoistway',
    section: 'pit',
    label: 'High water alarm?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 7,
  },
  {
    id: 'P8',
    level: 'hoistway',
    section: 'pit',
    label: 'New guarded LED lighting?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 8,
  },
  {
    id: 'P9a',
    level: 'hoistway',
    section: 'pit',
    label: 'Are any pit ladders being replaced?',
    hint: 'Say Yes and the form asks how many. There is one ladder per car in this hoistway.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 9,
  },
  {
    id: 'P9b',
    level: 'hoistway',
    section: 'pit',
    label: 'Are any pit ladders being modified?',
    // Q24–25 / §4 rule 6: this is a ≤ rule, NOT the exact-equality rule the
    // disconnects use. Leftover ladders are code-compliant and stay as they are.
    hint: 'Replaced + modified can add up to less than the car count — leftover ladders are code-compliant and stay as they are.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'ea',
    required: true,
    order: 10,
  },
  {
    id: 'P10',
    level: 'hoistway',
    section: 'pit',
    label: 'Light switch relocated?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 11,
  },
  {
    id: 'P11',
    level: 'hoistway',
    section: 'pit',
    label: 'Waterproof the pit?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 12,
  },
  // P12 "Paint the pit?" (bool, FLAT, order 13) was DELETED by Chip on
  // 2026-10-27 (D-053): painting the pit is the elevator company's scope, not
  // OSB's. The id is not reused; stored answers stay inert.
  {
    id: 'P13',
    level: 'hoistway',
    section: 'pit',
    label: 'Core drilling?',
    kind: 'bool',
    pricing: 'FLAT',
    required: true,
    order: 14,
  },
  // P14 "Fire caulking?" (bool, FLAT, order 15) was DELETED by Chip on
  // 2026-10-27 (D-053): the hoistway's fire caulking is asked once, overhead
  // (O8), and covers the pit. The id is not reused; stored answers stay inert.
];

/* ═════════════════════════ §3.9 Cab Interior ══════════════════════════════ */

const CAB: QuestionDef[] = [
  {
    id: 'C1',
    level: 'room',
    section: 'cab',
    label: 'Is any car getting new cab flooring?',
    // Q28: flooring is the ONLY cab item OSB prices. Everything else in the cab
    // belongs to other trades — no PRICED cab question goes here. F16 is not
    // priced: it is the fire alarm company's, read off the COP (Amendment 8).
    hint: 'Say Yes and the form asks how many cars. Flooring is the only cab item OSB prices — everything else in the cab is other trades.',
    kind: 'flagCount',
    pricing: 'SURV-QTY',
    uom: 'car',
    required: true,
    order: 1,
  },
  {
    id: 'F16',
    level: 'room',
    section: 'cab',
    // Amendment 8. Read off the car operating panel — the FIRE key switch and
    // the "Firefighters' Operation" placard. A `select`, not a `tristate`:
    // tristate is in DEFAULTABLE_KINDS and would be written Unknown by the form
    // before anyone looked; the design authority ruled these open blank and
    // must be picked (2026-09-06). Unknown, once picked, is a real answer.
    label: 'Is fire service recall (Phase I/II) already on these cars?',
    hint: 'Read it off the car operating panel.',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'unknown', label: 'Unknown' },
    ],
    required: true,
    order: 2,
  },
];

/* ══════════════════════════════ §3.10 Access ══════════════════════════════ */

const ACCESS: QuestionDef[] = [
  {
    id: 'A1',
    level: 'room',
    section: 'access',
    // D-053 (Chip, 2026-10-27): was "Is a crane required?". OSB does not hire
    // the crane — the office prices the crane company — it prices its own
    // crew's time and the plywood while a crane picks: a crane ASSIST. Still a
    // yes/no, still the gate for A2 and the staging-area shot.
    label: 'Is a crane assist needed?',
    // Q27: informational flag. Photos of the staging area are the point — the
    // crane company quotes from them later.
    hint: 'Flag it, add notes, and photograph the staging area.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 1,
  },
  {
    id: 'A2',
    level: 'room',
    section: 'access',
    label: 'How many picks?',
    // Gated on A1 because a pick count without a crane is meaningless — §3.10
    // shows no explicit gate, but §2.4 already treats the crane flag as the
    // gate for the staging-area shot, so the same reading is applied here.
    // Reported as a resolved ambiguity. Q27 had this as a best guess and never
    // a gap; D-053 (Chip, 2026-10-27): "We'll need to know the number of
    // picks, but material won't change for each pick, just labor" — so it is
    // required whenever A1 is Yes. Still INFO here: the pricing catalog, not
    // this file, holds the per-pick labor. D-055: at least 1, like G9a, G14a
    // and A3a — a crane assist with no picks is A1 = No.
    hint: 'Count every pick. Our crew is on site for each one, so the labor goes by the pick.',
    kind: 'number',
    pricing: 'INFO',
    uom: 'pick',
    showWhen: [{ questionId: 'A1', equals: true }],
    required: true,
    min: 1,
    order: 2,
  },
  {
    id: 'A3',
    level: 'room',
    section: 'access',
    label: 'Roof protection needed?',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 3,
  },
  {
    id: 'A3a',
    level: 'room',
    section: 'access',
    // D-054 (Chip, 2026-10-27): roof protection is laid in 4x8 plywood sheets,
    // counted when A3 is Yes. INFO, like A2 — the pricing catalog holds the
    // per-sheet price. At least 1: no sheets is A3 = No.
    label: 'How many 4x8 plywood sheets?',
    kind: 'number',
    pricing: 'INFO',
    uom: 'sheet',
    showWhen: [{ questionId: 'A3', equals: true }],
    required: true,
    min: 1,
    order: 3.5,
  },
];

/* ══════════════════ §3.11 Fire Life Safety (Amendment 5) ══════════════════ */

/**
 * FLS — the section the first interview missed, added from Chip's second
 * interview and handed down 2026-08-07 (spec Amendment 5, DECISIONS D-024).
 *
 * ── The id scheme ────────────────────────────────────────────────────────────
 * One prefix, **F**, numbered `F1…F12` in WALK ORDER — the panel first, then the
 * machine room, then the hoistway, then the pit, then the lobbies — with the
 * usual lowercase suffix for a follow-up that belongs to its parent (`F11a` is
 * *which* landings F11 is about). `F13` (overhead detectors) and `F14` (machine
 * room detectors) were added on 2026-08-19 to cover the FLS company's RFQ
 * template; they take the next numbers rather than renumbering anything, because
 * an id is a permanent key. That is the §3 convention exactly: a single
 * letter for the block, a number for the question, a letter for its child. The
 * numbers are contiguous across entity levels rather than restarting per level,
 * because an id is a permanent key and not a coordinate — F9 is F9 whether you
 * meet it in a hoistway or read it off an estimate.
 *
 * ── Where the block lives: one section for the panel, four for the rest ──────
 * FLS is not one place. It is a panel in the lobby, two relays in the machine
 * room, a sprinkler head in a shaft, three devices in a pit, and a smoke
 * detector at twenty landings. A `SectionKind` is generated at ONE entity level,
 * so the per-room, per-hoistway and per-group questions (F6–F12b) live in the
 * section that repeats at their own level, and the existing generation
 * machinery repeats them correctly for free.
 *
 * The panel and its paperwork — F1–F5 — ARE one place, and Amendment 5 put them
 * in the wrong one. They were filed on the building section, which is set up
 * at the truck at the START of a job; F3–F5 are read off the panel, and F1 has
 * nothing to count until the groups exist. Chip, field test, 2026-08-18:
 *
 *   *"Move the Fire Life Safety (FLS) section to its own standalone area,
 *    similar to Electrical and HVAC, and remove it from the building‑setup
 *    screen. Place it among the final sections of the survey, ideally the last,
 *    so the Surveyor can close and submit only after completing it."*
 *
 * So F1–F5 are filed on `fls` — a survey-level section generated once, after
 * every machine room and before the ad hoc checklist (core/sections.ts) — and
 * the walk gate withholds Close & submit until it has been walked
 * (ui/state/guide.ts). Their LEVEL is still `building` (one panel per job) and
 * their answer keys are unchanged: `section` is where a question is ASKED,
 * `level` is where its answer is KEYED, and only the first moved. Grep `F` +
 * the section header to read the block as a block.
 *
 * ── What this block is FOR (and what it is not) ─────────────────────────────
 * Everything here except F1 and F2 is INFO: it feeds the FLS subcontractor's
 * quote, and **the photos are the product**. So the text fields are not
 * `required` — a surveyor who photographs the panel and the sticker has done the
 * job even if the model number is unreadable in the dark, and calling that a gap
 * would be the form demanding tidiness it does not need (D-014/D-015). The Y/N
 * questions ARE required: they carry a written No by default (D-016), so they
 * cost nothing and yet come back as real answers rather than silence.
 *
 * F1 (back boxes) and F2 (the subcontractor proposal) are the two that touch
 * money, and neither is a surveyor's to enter.
 */
const FLS: QuestionDef[] = [
  {
    id: 'F1',
    level: 'building',
    section: 'fls',
    label: 'FLS back boxes',
    // AUTO and UNCONDITIONAL: one riser per group is assumed, and every landing
    // that riser passes gets a back box. Counted across every group in every
    // machine room, so this is the one BUILDING-scoped derived line that reaches
    // down into room structure. See flsBackBoxLine() in calc.ts.
    hint: 'Counted for you — one at every landing, added up across all the groups in this building.',
    kind: 'number',
    pricing: 'AUTO',
    uom: 'box',
    auto: true,
    order: 1,
  },
  {
    id: 'F2',
    level: 'building',
    section: 'fls',
    label: 'Fire Life Safety subcontractor — anything they should know?',
    // §4 rule 10, extended by Amendment 5. Every estimate carries an FLS
    // subcontractor line whether or not anything is typed here; ruleFlsProposal
    // in validation.ts is the marker that keeps it from being dropped, exactly
    // the way ruleChilledWater keeps H4's line. NOT required: the office gets
    // the number from the FLS company, and this box is context for them.
    hint: 'Every job carries an FLS subcontractor line — the office gets the price from the FLS company. Write down anything that would change their number, and photograph the panel and its sticker.',
    kind: 'text',
    pricing: 'OFFICE',
    order: 2,
  },
  {
    id: 'F3',
    level: 'building',
    section: 'fls',
    label: 'Fire alarm panel (FACP) — make and model',
    hint: 'Read it off the panel. If you cannot make it out, photograph it and move on — the picture is what the FLS company works from.',
    kind: 'text',
    pricing: 'INFO',
    order: 3,
  },
  {
    id: 'F4',
    level: 'building',
    section: 'fls',
    // Same pairing as B6/B7 (elevator company, then the rep): the company on one
    // key, the person on another, so neither has to be dug back out of the other.
    label: 'Fire alarm company servicing this building',
    hint: "It is on the sticker on the panel. Photograph the sticker — that's the required shot.",
    kind: 'text',
    pricing: 'INFO',
    order: 4,
  },
  {
    id: 'F5',
    level: 'building',
    section: 'fls',
    label: 'Fire alarm company contact',
    hint: 'Whatever the sticker gives you — a name, a phone number, or both.',
    kind: 'contact',
    pricing: 'INFO',
    order: 5,
  },
  {
    id: 'F18',
    level: 'building',
    section: 'fls',
    // Amendment 8. Asked once for the building. A Yes can be folded from M4 /
    // F9 / F10 / F12; a building-level No or Unknown cannot — a building can be
    // sprinklered somewhere the surveyor never went. `select`, not `tristate`:
    // see F16.
    label: 'Does the building have a sprinkler system?',
    hint: 'Yes, No, or Unknown if you could not tell.',
    kind: 'select',
    pricing: 'INFO',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'unknown', label: 'Unknown' },
    ],
    required: true,
    order: 6,
  },
  // F19 — "How does the fire alarm company get in?" (Amendment 8, building
  // level, order 7) — was DELETED by Chip on 2026-09-11 (D-045). The id is not
  // reused. Answers already stored under `F19` are left where they are and the
  // cut sheet / vendor form still print them (`panel.access`, `accessNotes`);
  // a survey started on this build simply never asks, so those read null.
  {
    id: 'F6',
    level: 'room',
    section: 'electrical',
    label: 'Is there a heat detector within 18 inches of the sprinkler head?',
    // Sprinklered rooms only — there is no head to measure from otherwise. Same
    // gate as the whole E4 shunt block (M4), read in the room's own scope.
    hint: 'Only asked because this room has a sprinkler. Photograph the head and the detector together if you can.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'M4', equals: true }],
    required: true,
    order: 27,
  },
  {
    id: 'F7',
    level: 'room',
    section: 'electrical',
    label: 'Is there a shunt relay in this machine room?',
    hint: 'Only asked because this room has a sprinkler.',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'M4', equals: true }],
    required: true,
    order: 28,
  },
  {
    id: 'F8',
    level: 'room',
    section: 'electrical',
    label: 'Which fire service relays are in this machine room?',
    // ALWAYS asked — a dry machine room still has Primary / Secondary / Flashing
    // Hat relays. Only the Shunt box is gated, on the option itself (D-024), so
    // one inventory keeps one id in every room. `optionsFor()` in validation.ts
    // is what a screen must call to see the narrowing.
    hint: 'Tick every one you can see. Photograph the relays — the FLS company quotes off the picture.',
    kind: 'multiselect',
    pricing: 'INFO',
    options: [
      { value: 'primary', label: 'Primary' },
      { value: 'secondary', label: 'Secondary' },
      { value: 'flashing_hat', label: 'Flashing hat' },
      {
        value: 'shunt',
        label: 'Shunt',
        hint: 'Only in a sprinklered machine room.',
        showWhen: [{ questionId: 'M4', equals: true }],
      },
    ],
    order: 29,
  },
  {
    id: 'F14',
    level: 'room',
    section: 'electrical',
    // Amendment 7 (2026-08-19, D-027). Chip's RFQ template asks the FLS company's
    // question outright — "Existing Devices Machine Room: Smoke(s) Heat(s)
    // Sprinkled" — and until now the form asked only about the sprinkler (M4)
    // and the one heat detector within 18" of its head (F6). The inventory is
    // the cut sheet's line, so it is asked as one. Not required: a room with
    // none ticks nothing, and an untouched box is not a claim.
    label: 'Which fire and life safety devices are in this machine room?',
    hint: 'Tick every one you can see. The sprinkler itself is the room question above; this is the detectors. Recorded for the FLS company.',
    kind: 'multiselect',
    pricing: 'INFO',
    options: [
      { value: 'smoke', label: 'Smoke detector(s)' },
      { value: 'heat', label: 'Heat detector(s)' },
    ],
    order: 30,
  },
  {
    id: 'F20',
    level: 'room',
    section: 'electrical',
    // Amendment 8. E4a says where the shunts GO (external / in-disconnect);
    // F7 says whether a relay EXISTS; nothing said WHERE. Same gate as F6/F7.
    label: 'Where is the shunt disconnect, or where will it go?',
    hint: 'Only asked because this room has a sprinkler.',
    kind: 'text',
    pricing: 'INFO',
    showWhen: [{ questionId: 'M4', equals: true }],
    order: 31,
  },
  {
    id: 'F21',
    level: 'room',
    section: 'electrical',
    // Amendment 8. The company's report read "Elevator #6 Power, Feeder Panel
    // EDP #2, A Level" off a handwritten label; this is that sentence's box.
    // D-045 (Chip, 2026-09-11): was "What feeds the main disconnect?".
    label: 'Where is mainline disconnect fed from?',
    hint: 'Panel name, breaker, floor.',
    kind: 'text',
    pricing: 'INFO',
    order: 32,
  },
  {
    id: 'F9',
    level: 'hoistway',
    section: 'overhead',
    label: 'Are there sprinklers in this hoistway?',
    hint: 'Anywhere in the shaft, not just at the top. Recorded for the FLS company.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 10,
  },
  {
    id: 'F13',
    level: 'hoistway',
    section: 'overhead',
    // Amendment 7 (2026-08-19, D-027). The RFQ template's "Existing Devices Overhead:
    // Smoke(s) Heat(s) Sprinkled" — F9 answers "Sprinkled"; this is the other
    // two, at the top of the hoistway where the overhead section stands.
    label: 'Which fire and life safety devices are at the top of this hoistway?',
    hint: 'Smoke and heat detectors up top, if any. The sprinkler question is just above. Recorded for the FLS company.',
    kind: 'multiselect',
    pricing: 'INFO',
    options: [
      { value: 'smoke', label: 'Smoke detector(s)' },
      { value: 'heat', label: 'Heat detector(s)' },
    ],
    order: 11,
  },
  {
    id: 'F10',
    level: 'hoistway',
    section: 'pit',
    label: 'Which fire and life safety devices are in this pit?',
    hint: 'Tick every one you find. Nothing here is priced — it goes to the FLS company with your pit photos.',
    kind: 'multiselect',
    pricing: 'INFO',
    options: [
      { value: 'sprinkler', label: 'Sprinkler' },
      { value: 'heat', label: 'Heat detector' },
      { value: 'smoke', label: 'Smoke detector' },
    ],
    order: 16,
  },
  {
    id: 'F11',
    level: 'group',
    section: 'lobby',
    // EXCEPTION-BASED, and the polarity is deliberate. Chip's words were "all
    // lobbies have smoke detectors? — NO reveals the missing landings", which is
    // the same question asked from the other end. Asked THIS way round, the
    // written default (D-016) is No — "nothing missing" — which is both the
    // common case and the calm one: a question nobody has touched does not
    // unfold a range editor about landings nobody has said are missing (D-014).
    label: 'Are any landings in this group missing a smoke detector?',
    hint: 'Spot-check the lobbies while you are riding — you do not have to walk every floor. No means every landing has one.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 14,
  },
  {
    id: 'F11a',
    level: 'group',
    section: 'lobby',
    label: 'Which landings are missing a smoke detector?',
    // Same range machinery as L4a — "3–7" is one row, not five taps. These rows
    // carry no wall type; see WallRange in types.ts.
    hint: 'Enter ranges — "3–7" is one row. Landings only; there is no material to pick here.',
    kind: 'ranges',
    pricing: 'INFO',
    uom: 'landing',
    showWhen: [{ questionId: 'F11', equals: true }],
    required: true,
    order: 15,
  },
  {
    id: 'F12',
    level: 'group',
    section: 'lobby',
    label: 'Are any lobbies in this group sprinklered?',
    hint: 'Spot-check while you ride. Yes asks which landings.',
    kind: 'bool',
    pricing: 'INFO',
    required: true,
    order: 16,
  },
  {
    id: 'F12a',
    level: 'group',
    section: 'lobby',
    label: 'Which landings are sprinklered?',
    hint: 'Enter ranges — "1–3" is one row.',
    kind: 'ranges',
    pricing: 'INFO',
    uom: 'landing',
    showWhen: [{ questionId: 'F12', equals: true }],
    required: true,
    order: 17,
  },
  {
    id: 'F12b',
    level: 'group',
    section: 'lobby',
    label: 'Do those sprinklered lobbies have heat detectors?',
    kind: 'bool',
    pricing: 'INFO',
    showWhen: [{ questionId: 'F12', equals: true }],
    required: true,
    order: 18,
  },
  {
    id: 'F17',
    level: 'group',
    section: 'lobby',
    // Amendment 8. The company's form has three boxes — Smoke / Heat / Other —
    // and F11/F12b could only say "a smoke is missing" or "the sprinklered
    // lobbies have heats". Same shape as F13/F14. Not required: an untouched
    // box is not a claim.
    label: 'What kind of detectors are at the landings?',
    hint: 'Tick what you see. Say where in the notes if it differs by floor.',
    kind: 'multiselect',
    pricing: 'INFO',
    options: [
      { value: 'smoke', label: 'Smoke' },
      { value: 'heat', label: 'Heat' },
      { value: 'other', label: 'Other' },
    ],
    order: 19,
  },
];

/* ═══════════════════════════════ the catalog ══════════════════════════════ */

/**
 * Every question in specification §3, in walk order.
 *
 * FLS (§3.11, Amendment 5) is one array but reaches five sections; `questionsFor`
 * sorts by `order` within a section, and every FLS question is numbered after
 * the last question of the section it joins, so each one lands as a block at the
 * end of its section.
 */
export const QUESTIONS: QuestionDef[] = [
  ...BUILDING,
  ...ROOM_SETUP,
  ...ELECTRICAL,
  ...HVAC,
  ...GENERAL,
  ...OVERHEAD,
  ...LOBBY,
  ...PIT,
  ...CAB,
  ...ACCESS,
  ...FLS,
];

const BY_ID: ReadonlyMap<string, QuestionDef> = new Map(QUESTIONS.map((q) => [q.id, q]));

/** Look a question up by its specification ID. */
export function byId(id: string): QuestionDef | undefined {
  return BY_ID.get(id);
}

/**
 * Questions for one section, in walk order.
 *
 * Pass `formSet` to drop the questions that do not belong to this room's set —
 * G11–G17 vanish in a Hydro room, E14 and P6 vanish in a Traction room. Omit it
 * (building, ad hoc, or any survey-wide listing) and nothing is filtered out.
 *
 * This does NOT evaluate `showWhen`; gating is the form's job because it needs
 * the answer map. This only answers "which questions could this section ask?".
 */
export function questionsFor(section: SectionKind, formSet?: FormSet): QuestionDef[] {
  return QUESTIONS.filter(
    (q) => q.section === section && (!q.formSets || !formSet || q.formSets.includes(formSet)),
  ).sort((a, b) => a.order - b.order);
}

/**
 * The scope in which a `showWhen` condition must be evaluated.
 *
 * QuestionCondition documents the referenced answer as "resolved in the same
 * scope". That is right for every same-level gate — E4a/E4b/E4c gate on M4 and
 * all four are room-level — but it is WRONG for the one cross-level gate in the
 * specification: L4 is a per-GROUP lobby question gated on B8, which is a
 * BUILDING-level answer. Resolving B8 in the group's scope asks for
 * "room:r1|grp:gA|B8", a key that can never exist, so the gate would read as
 * permanently closed and L4/L4a would silently vanish on every consultant job —
 * exactly the case spec §3.7 says they exist for.
 *
 * A condition is therefore resolved at the level of the REFERENCED question,
 * narrowing the asking question's scope to the segments that level uses. Every
 * gate evaluator must call this rather than reusing the asking scope verbatim.
 *
 *   conditionScope({ questionId: 'B8' }, { roomId: 'r1', groupId: 'gA' }) -> {}
 *   conditionScope({ questionId: 'M4' }, { roomId: 'r1', groupId: 'gA' }) -> { roomId: 'r1' }
 */
export function conditionScope(condition: QuestionCondition, scope: Scope): Scope {
  const ref = BY_ID.get(condition.questionId);
  // Structural predicates and unknown ids carry no answer to look up; hand the
  // scope back untouched so the caller's own resolution still works.
  if (!ref) return { ...scope };

  const room: Scope = scope.roomId ? { roomId: scope.roomId } : {};
  switch (ref.level) {
    case 'building':
    case 'survey':
      return {};
    case 'room':
      return room;
    case 'group':
      return scope.groupId ? { ...room, groupId: scope.groupId } : room;
    case 'hoistway':
      return scope.hoistwayId ? { ...room, hoistwayId: scope.hoistwayId } : room;
  }
}

/**
 * The options to render for a question in a given form set.
 *
 * Kept as a one-line convenience over the tag that now lives on each option
 * (DECISIONS D-007). Unlike the lookup table it replaced, this holds no
 * knowledge of its own — a caller reading `question.options` and filtering
 * inline gets exactly the same answer.
 *
 * ── An option can also carry a GATE (D-024) ──────────────────────────────────
 * F8's **Shunt** box exists only in a sprinklered machine room, and a gate needs
 * the document to evaluate. This file has no document, so `gate` is how one is
 * supplied: `optionsFor(question, doc, scope)` in validation.ts passes the same
 * evaluator every `showWhen` in the catalog goes through, and that is what a
 * screen should call.
 *
 * With no `gate`, a conditional option is DROPPED. That is the rule both engines
 * already apply to a question's own gates — an unanswered gate is a closed gate
 * — and it is the safe direction here too: showing Shunt in a dry machine room
 * invites a tick that asserts a relay nobody could have seen.
 */
export function visibleOptions(
  question: QuestionDef,
  formSet: FormSet,
  gate?: (condition: QuestionCondition) => boolean,
): QuestionOption[] {
  return (question.options ?? []).filter((o) => {
    if (o.formSets && !o.formSets.includes(formSet)) return false;
    if (!o.showWhen || o.showWhen.length === 0) return true;
    if (!gate) return false;
    return o.showWhen.every((condition) => gate(condition));
  });
}
